import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../../../App";
import {
  fetchAddonCategorySummary,
  fetchAddonsByCategory,
  fetchAccountByOrg,
} from "../../../../../../config/Services";
import "./index.css";

/* ══════════════════════════════════════════════════════════
   Constants
   ══════════════════════════════════════════════════════════ */
const CATEGORY_COLORS = {
  "Pallet Apps":         "#1662E8",
  "Accounting":          "#16A34A",
  "Aggregators":         "#7C3AED",
  "Billing":             "#0EA5E9",
  "Logistics":           "#F97316",
  "Marketing":           "#EC4899",
  "Property Management": "#6B7280",
  "Sales Channel":       "#D97706",
};
const DEFAULT_CAT_COLOR = "#1662E8";
const getCatColor = (cat) => CATEGORY_COLORS[cat] || DEFAULT_CAT_COLOR;

/* ══════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════ */
function inrFormat(amount) {
  if (amount == null) return null;
  const n = Number(amount);
  if (isNaN(n) || n === 0) return null;
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1).replace(/\.0$/, "")}Cr`;
  if (n >= 100_000)    return `₹${(n / 100_000).toFixed(1).replace(/\.0$/, "")}L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function getMonogram(name) {
  if (!name) return "??";
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

/* ══════════════════════════════════════════════════════════
   Sub-components
   ══════════════════════════════════════════════════════════ */
function AddonLogo({ addon, size = 44, radius = 10 }) {
  const [failed, setFailed] = useState(false);
  const color = getCatColor(addon.category);
  const style = { width: size, height: size, borderRadius: radius };
  if (addon.logoUrl && !failed) {
    return (
      <img
        className="ai-addon-logo"
        src={addon.logoUrl}
        alt={addon.packageName}
        style={style}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div className="ai-addon-monogram" style={{ ...style, background: color }}>
      {getMonogram(addon.packageName)}
    </div>
  );
}

function StatusBadge({ addon }) {
  if (addon.implementationStatus && addon.status === "ACTIVE") {
    return <span className="ai-badge ai-badge-green">✓ Connected</span>;
  }
  if (addon.status !== "ACTIVE") {
    return <span className="ai-badge ai-badge-amber">Inactive</span>;
  }
  return <span className="ai-badge ai-badge-gray">Available</span>;
}

function PriceBadge({ addon }) {
  if (!addon.chargeable) {
    return <span className="ai-badge ai-badge-free">Free</span>;
  }
  const opts = addon.pricingOptions || [];
  const monthly = opts.find((o) => o.billingCycle === "MONTHLY" && o.status === "ACTIVE");
  const annual  = opts.find((o) => o.billingCycle === "ANNUAL"  && o.status === "ACTIVE");
  const chosen  = monthly || annual;
  if (chosen) {
    const amt   = inrFormat(chosen.netPrice);
    const label = amt
      ? `${amt}/${chosen.billingCycle === "MONTHLY" ? "mo" : "yr"}`
      : chosen.billingCycle === "MONTHLY" ? "Monthly" : "Annual";
    return <span className="ai-badge ai-badge-blue">{label}</span>;
  }
  return <span className="ai-badge ai-badge-blue">Paid</span>;
}

function SkeletonCards() {
  return Array.from({ length: 4 }, (_, i) => (
    <div className="ai-skeleton-card" key={i}>
      <div className="ai-skel-logo" />
      <div className="ai-skel-body">
        <div className="ai-skel-line" style={{ width: "52%" }} />
        <div className="ai-skel-line" style={{ width: "78%" }} />
        <div className="ai-skel-line" style={{ width: "32%" }} />
      </div>
    </div>
  ));
}

/* ══════════════════════════════════════════════════════════
   Context resolver
   ══════════════════════════════════════════════════════════ */
function resolveAuth() {
  const orgId      = localStorage.getItem("orgId")   || "";
  const locId      = localStorage.getItem("locId")   || "";
  const accountId  = localStorage.getItem("accountId") || "";
  const retailType = localStorage.getItem("retailType") || "";
  const planCategory = retailType === "RESTAURANT" ? "RESTAURANT" : "GROCERY";
  return { orgId, locId, accountId, planCategory };
}

/* ══════════════════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════════════════ */
export default function AppsIntegrations() {
  const navigate = useNavigate();
  const { color } = useContext(UserContext);
  const isDark = color === "dark";

  /* Auth */
  const { orgId, locId, planCategory } = resolveAuth();
  const [accountId, setAccountId] = useState(resolveAuth().accountId);

  /* State */
  const [categories,     setCategories]     = useState([]);
  const [catsLoading,    setCatsLoading]    = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [trueTotal,      setTrueTotal]      = useState(null);
  const [trueInstalled,  setTrueInstalled]  = useState(null);
  const [addons,         setAddons]         = useState([]);
  const [addonsLoading,  setAddonsLoading]  = useState(true);
  const [currentPage,    setCurrentPage]    = useState(0);
  const [totalPages,     setTotalPages]     = useState(1);
  const [loadingMore,    setLoadingMore]    = useState(false);
  const [search,         setSearch]         = useState("");
  const [committedSearch, setCommittedSearch] = useState("");

  /* Stale-request guards */
  const activeCatRef        = useRef("All");
  const activeSearchRef     = useRef("");
  const resolvedAccountRef  = useRef(resolveAuth().accountId);

  /* Skip the initial [activeCategory, committedSearch] effect fire */
  const initializedRef = useRef(false);

  /* ── Search debounce 400 ms ─────────────────────────── */
  useEffect(() => {
    const t = setTimeout(() => setCommittedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  /* ── Re-fetch when category/search changes (after init) ─ */
  useEffect(() => {
    if (!initializedRef.current) return;
    loadPage0(activeCategory, committedSearch);
  }, [activeCategory, committedSearch]); // eslint-disable-line

  /* ── Mount: resolve accountId + parallel init calls ──── */
  useEffect(() => {
    initLoad();
  }, []); // eslint-disable-line

  const initLoad = async () => {
    /* Resolve accountId if not yet cached */
    let aid = localStorage.getItem("accountId") || "";
    if (!aid && orgId) {
      try {
        const r  = await fetchAccountByOrg(orgId);
        const id = r?.data?.data?.data?.accountId || "";
        if (id) {
          aid = id;
          localStorage.setItem("accountId", id);
          setAccountId(id);
        }
      } catch (_) { /* non-fatal */ }
    }
    resolvedAccountRef.current = aid;

    setCatsLoading(true);

    const basePayload = {
      pageSize:       50,
      pageNo:         0,
      accountId:      aid,
      organizationId: orgId,
      locationId:     locId,
      planCategory,
    };

    /* Parallel: category list + full "All" page for true totals */
    const [catRes, allRes] = await Promise.allSettled([
      fetchAddonCategorySummary(basePayload),
      fetchAddonsByCategory({ ...basePayload, pageSize: 500, planCategory, searchBox: null }),
    ]);

    if (catRes.status === "fulfilled") {
      const cats = catRes.value?.data?.data?.data?.data ?? [];
      setCategories(Array.isArray(cats) ? cats : []);
    }

    if (allRes.status === "fulfilled") {
      const items = allRes.value?.data?.data?.data?.addons ?? [];
      const seen  = new Set();
      let tot = 0, inst = 0;
      for (const a of items) {
        if (!seen.has(a.addonId)) {
          seen.add(a.addonId);
          tot++;
          if (a.implementationStatus && a.status === "ACTIVE") inst++;
        }
      }
      setTrueTotal(tot);
      setTrueInstalled(inst);
    }

    setCatsLoading(false);

    /* Now allow the effect to react to future cat/search changes */
    initializedRef.current = true;

    /* Kick off the initial card list */
    await loadPage0("All", "", aid);
  };

  /* ── Load page 0 for a given cat + search ───────────── */
  const loadPage0 = useCallback(async (cat, srch, aidOverride) => {
    const aid = aidOverride ?? resolvedAccountRef.current;
    activeCatRef.current    = cat;
    activeSearchRef.current = srch;

    setAddonsLoading(true);
    setCurrentPage(0);

    try {
      const payload = {
        pageSize:       10,
        pageNo:         0,
        accountId:      aid,
        organizationId: orgId,
        locationId:     locId,
        planCategory,
        searchBox:      srch || null,
      };
      if (cat !== "All") payload.category = cat;

      const res  = await fetchAddonsByCategory(payload);

      /* Stale check */
      if (activeCatRef.current !== cat || activeSearchRef.current !== srch) return;

      const data = res?.data?.data?.data;
      setAddons(data?.addons    ?? []);
      setTotalPages(data?.totalPage ?? 1);
    } catch (_) {
      if (activeCatRef.current !== cat || activeSearchRef.current !== srch) return;
      setAddons([]);
    } finally {
      if (activeCatRef.current === cat && activeSearchRef.current === srch) {
        setAddonsLoading(false);
      }
    }
  }, [orgId, locId, planCategory]); // eslint-disable-line

  /* ── Load next page ─────────────────────────────────── */
  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    try {
      const payload = {
        pageSize:       10,
        pageNo:         nextPage,
        accountId:      resolvedAccountRef.current,
        organizationId: orgId,
        locationId:     locId,
        planCategory,
        searchBox:      committedSearch || null,
      };
      if (activeCategory !== "All") payload.category = activeCategory;

      const res  = await fetchAddonsByCategory(payload);
      const data = res?.data?.data?.data;
      setAddons((prev) => [...prev, ...(data?.addons ?? [])]);
      setCurrentPage(nextPage);
    } catch (_) { /* swallow */ }
    setLoadingMore(false);
  };

  /* ── Category change ────────────────────────────────── */
  const handleCategoryChange = (cat) => {
    if (cat === activeCategory) return;
    setSearch("");
    setCommittedSearch("");
    setActiveCategory(cat);
  };

  /* ── Card click → detail ────────────────────────────── */
  const handleCardClick = (addon) => {
    navigate("/settings/app-detail", { state: { addon } });
  };

  /* ── Derived ────────────────────────────────────────── */
  const totalApps      = trueTotal      ?? categories.reduce((s, c) => s + (c.total     || 0), 0);
  const totalInstalled = trueInstalled  ?? categories.reduce((s, c) => s + (c.installed || 0), 0);

  const connectedAddons = addons.filter((a) => a.implementationStatus && a.status === "ACTIVE");
  const showConnected   = !committedSearch && !addonsLoading && connectedAddons.length > 0;
  const isPending       = search !== committedSearch;
  const subtitle        = catsLoading
    ? "Loading…"
    : `${totalApps} apps · ${totalInstalled} connected`;

  /* ════════════════════════════════════════════════════════
     Render
     ════════════════════════════════════════════════════════ */
  return (
    <div className={`ai-container${isDark ? " dark" : ""}`}>

      {/* ────────────────────────────────────────────────
          Gradient Header
          ──────────────────────────────────────────────── */}
      <div className="ai-header">

        {/* Title row */}
        <div className="ai-header-top">
          <button className="ai-back" onClick={() => navigate(-1)} aria-label="Go back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <div>
            <p className="ai-header-title">Apps &amp; Integrations</p>
            <p className="ai-header-sub">{subtitle}</p>
          </div>
        </div>

        {/* Search bar */}
        <div className="ai-search-bar">
          <svg className="ai-search-icon" width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            className="ai-search-input"
            placeholder="Search apps and integrations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isPending && <span className="ai-spinner-blue" />}
          {search.length > 0 && !isPending && (
            <button className="ai-search-clear" onClick={() => setSearch("")} aria-label="Clear search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" />
              </svg>
            </button>
          )}
        </div>

        {/* Stats strip */}
        <div className="ai-stats-strip">
          <div className="ai-stat-cell ai-stat-cell-blue">
            <span className="ai-stat-value">{catsLoading ? "—" : totalApps}</span>
            <span className="ai-stat-label">Total</span>
          </div>
          <div className="ai-stat-cell ai-stat-cell-green">
            <span className="ai-stat-value">{catsLoading ? "—" : totalInstalled}</span>
            <span className="ai-stat-label">Connected</span>
          </div>
          <div className="ai-stat-cell ai-stat-cell-navy">
            <span className="ai-stat-value">{catsLoading ? "—" : categories.length}</span>
            <span className="ai-stat-label">Categories</span>
          </div>
        </div>

        {/* Category tabs */}
        <div className="ai-tabs-scroll">
          {["All", ...categories.map((c) => c.category)].map((cat) => {
            const isActive  = cat === activeCategory;
            const catData   = categories.find((c) => c.category === cat);
            const catColor  = getCatColor(cat);
            return (
              <button
                key={cat}
                className={`ai-tab-pill${isActive ? " active" : ""}`}
                onClick={() => handleCategoryChange(cat)}
              >
                {cat}
                {catData && (
                  <span
                    className="ai-tab-badge"
                    style={isActive ? { background: catColor + "22", color: catColor } : {}}
                  >
                    {catData.installed}/{catData.total}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ────────────────────────────────────────────────
          Scrollable Body
          ──────────────────────────────────────────────── */}
      <div className="ai-body">

        {/* Connected strip */}
        {showConnected && (
          <div className="ai-connected-strip">
            <div className="ai-connected-header">
              <div className="ai-connected-dot" />
              {connectedAddons.length} Active
            </div>
            <div className="ai-connected-chips">
              {connectedAddons.map((a) => {
                const cc = getCatColor(a.category);
                return (
                  <div key={a.addonId} className="ai-connected-chip">
                    {a.logoUrl ? (
                      <img className="ai-chip-mini-logo" src={a.logoUrl} alt={a.packageName} />
                    ) : (
                      <div className="ai-chip-mini-mono" style={{ background: cc }}>
                        {getMonogram(a.packageName)}
                      </div>
                    )}
                    {a.packageName}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Search results indicator */}
        {committedSearch && !addonsLoading && (
          <p className="ai-search-results-label">
            Results for <em>&ldquo;{committedSearch}&rdquo;</em>
            {activeCategory !== "All" && <> in <em>{activeCategory}</em></>}
          </p>
        )}

        {/* Skeletons / cards / empty state */}
        {addonsLoading ? (
          <SkeletonCards />
        ) : addons.length === 0 ? (
          <div className="ai-empty">
            <div className="ai-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <path d="M8 21h8M12 17v4" />
              </svg>
            </div>
            <p className="ai-empty-title">No apps found</p>
            <p className="ai-empty-sub">
              {committedSearch
                ? "Try a different search term"
                : "No apps available in this category"}
            </p>
          </div>
        ) : (
          addons.map((addon) => {
            const catColor = getCatColor(addon.category);
            return (
              <button
                key={addon.addonId}
                className="ai-addon-card"
                onClick={() => handleCardClick(addon)}
              >
                <div className="ai-addon-stripe" style={{ background: catColor }} />
                <div className="ai-addon-content">
                  <div className="ai-addon-top">
                    <AddonLogo addon={addon} />
                    <div className="ai-addon-info">
                      <p className="ai-addon-name">{addon.packageName}</p>
                      <p className="ai-addon-desc">
                        {addon.shortDescription || addon.description || ""}
                      </p>
                    </div>
                    <svg className="ai-addon-chevron" width="16" height="16" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </div>
                  <div className="ai-addon-footer">
                    <StatusBadge addon={addon} />
                    <PriceBadge  addon={addon} />
                    {addon.category && (
                      <span
                        className="ai-badge"
                        style={{ background: catColor + "18", color: catColor }}
                      >
                        {addon.category}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}

        {/* Load More */}
        {!addonsLoading && addons.length > 0 && currentPage + 1 < totalPages && (
          <div className="ai-load-more-row">
            <button
              className="ai-load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore && <span className="ai-spinner-blue" />}
              Load more apps
            </button>
            <span className="ai-pagination-info">
              Showing {addons.length} · page {currentPage + 1} of {totalPages}
            </span>
          </div>
        )}

        {/* Pagination info when all loaded */}
        {!addonsLoading && addons.length > 0 && currentPage + 1 >= totalPages && (
          <p className="ai-pagination-info" style={{ marginTop: 10 }}>
            Showing {addons.length} · page {currentPage + 1} of {totalPages}
          </p>
        )}
      </div>
    </div>
  );
}
